import { Chapter, Plan, OnboardingChecklist } from '@fsp/types';
import React, { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { usePlan as usePlanQuery, planKeys } from '../hooks/queries/usePlans';
import { useChapters, chapterKeys } from '../hooks/queries/useChapters';
import { updatePlan } from '../services/plan';

interface PlanContextType {
  currentPlan: Plan | null;
  currentPlanId: string | null;
  chapters: Chapter[];
  refreshCurrentPlan: () => void;
  refreshChapters: () => void;
  updateOnboardingTask: (taskId: keyof OnboardingChecklist) => Promise<void>;
  isLoading: boolean;
}

const PlanContext = createContext<PlanContextType | undefined>(undefined);

export const PlanProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { planId } = useParams<{ planId: string }>();
  const { currentOrganisation } = useAuth();
  const queryClient = useQueryClient();

  const { data: currentPlan, isLoading: isPlanLoading } = usePlanQuery(
    currentOrganisation?.id,
    planId
  );
  const { data: chapters = [], isLoading: isChaptersLoading } = useChapters(
    currentOrganisation?.id,
    planId
  );

  const refreshCurrentPlan = () => {
    if (currentOrganisation?.id && planId) {
      queryClient.invalidateQueries({ queryKey: planKeys.detail(currentOrganisation.id, planId) });
    }
  };

  const refreshChapters = () => {
    if (currentOrganisation?.id && planId) {
      queryClient.invalidateQueries({
        queryKey: chapterKeys.list(currentOrganisation.id, planId),
      });
    }
  };

  const updateOnboardingTask = async (taskId: keyof OnboardingChecklist) => {
    console.log('Updating onboarding task:', taskId);
    if (!currentOrganisation?.id || !planId || !currentPlan) return;

    try {
      let checklist: OnboardingChecklist;
      if (currentPlan.onboardingChecklist) {
        checklist = JSON.parse(currentPlan.onboardingChecklist);
      } else {
        checklist = {
          editFlowDiagram: false,
          identifyHazard: false,
          createPlanScope: false,
          createIncomingGoods: false,
          addFirstRecord: false,
        };
      }

      checklist[taskId] = true;

      await updatePlan(currentOrganisation.id, planId, {
        onboardingChecklist: JSON.stringify(checklist),
      });

      refreshCurrentPlan();
    } catch (error) {
      console.error('Failed to update onboarding task:', error);
    }
  };

  const value = useMemo(
    () => ({
      currentPlan: currentPlan || null,
      currentPlanId: planId || null,
      chapters,
      refreshCurrentPlan,
      refreshChapters,
      updateOnboardingTask,
      isLoading: isPlanLoading || isChaptersLoading,
    }),
    [currentPlan, planId, chapters, isPlanLoading, isChaptersLoading]
  );

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
};

export const usePlan = (): PlanContextType => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
};
